import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { conflictData, describeStockError } from '../lib/stock-errors'
import type { QueryClient } from '@tanstack/react-query'
import type {
  StockCorrectionRequired,
  StockUsageSaveRequest,
  StockUsageSaveResponse,
  StockUsageView,
} from '@/api/endpoints/product-stock'
import {
  productStockApi,
  stockUsageQueryKey,
} from '@/api/endpoints/product-stock'

/** The stock usage view of a product (tracking, parts, "Verwendet in", variants). */
export function useStockUsage(sku: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: stockUsageQueryKey(sku),
    queryFn: () => productStockApi.getStockUsage(sku),
    enabled: !!sku && (options?.enabled ?? true),
  })
}

type OnSaved = (res: StockUsageSaveResponse) => void

interface PendingCorrection {
  body: StockUsageSaveRequest
  details: StockCorrectionRequired
  /** The details came from a refused confirmation: the stock changed while the dialog was open. */
  refreshed: boolean
  onSaved?: OnSaved
}

/** Every stock query: the product's levels, the inventory list and every stock history. */
function isStockQuery(queryKey: ReadonlyArray<unknown>): boolean {
  const head = queryKey[0]
  return typeof head === 'string' && head.startsWith('inventory')
}

/**
 * After a save: the response is the new view, so it goes into the cache at once — the tab never
 * shows the old list (no flicker, no second save of the same list). The product's own queries
 * (detail badges, guard) are refetched before the save counts as done; everything else that can
 * show the change — lists, the parts' "Teilmengen zählen", levels and the history of a correction
 * to 0 — is refreshed in the background.
 */
async function applySaved(
  queryClient: QueryClient,
  sku: string,
  res: StockUsageSaveResponse,
) {
  const { corrections: _corrections, ...view } = res
  queryClient.setQueryData<StockUsageView>(stockUsageQueryKey(sku), view)
  void queryClient.invalidateQueries({
    predicate: (query) =>
      isStockQuery(query.queryKey) ||
      (query.queryKey[0] === 'products' && query.queryKey[1] !== sku),
  })
  await queryClient.invalidateQueries({
    queryKey: ['products', sku],
    predicate: (query) => query.queryKey[2] !== 'stock-usage',
  })
}

/**
 * Saves a product's stock mode with the confirm flow of D10 (task 10.4): the first request goes
 * without `confirmCorrection`; a `409 stock_correction_required` keeps the request and its
 * details in `pendingCorrection` for the confirmation dialog, and `confirmCorrection()` repeats
 * the same request with `confirmCorrection: true`. If that retry is refused again (the stock
 * changed in between), the dialog stays open with the fresh details. Any other refusal is shown
 * as a toast.
 */
export function useStockUsageSave(sku: string) {
  const queryClient = useQueryClient()
  const { t: tProducts } = useTranslation('products')
  const [pending, setPending] = useState<PendingCorrection | null>(null)

  const mutation = useMutation({
    mutationFn: (vars: { body: StockUsageSaveRequest; onSaved?: OnSaved }) =>
      productStockApi.saveStockUsage(sku, vars.body),
    onSuccess: async (res, vars) => {
      setPending(null)
      // The cache write inside applySaved runs synchronously, before onSaved leaves an edit mode.
      const refreshed = applySaved(queryClient, sku, res)
      vars.onSaved?.(res)
      await refreshed
    },
    onError: (err, vars) => {
      const details = conflictData<StockCorrectionRequired>(
        err,
        'stock_correction_required',
      )
      if (details) {
        const { confirmCorrection: _confirm, ...body } = vars.body
        setPending({
          body,
          details,
          refreshed: vars.body.confirmCorrection === true,
          onSaved: vars.onSaved,
        })
        return
      }
      setPending(null)
      toast.error(describeStockError(err, tProducts('stockUsage.saveFailed')))
    },
  })

  return {
    save: (body: StockUsageSaveRequest, onSaved?: OnSaved) =>
      mutation.mutate({ body, onSaved }),
    pendingCorrection: pending?.details ?? null,
    /** The open dialog shows details refreshed by a refused confirmation. */
    correctionRefreshed: pending?.refreshed ?? false,
    confirmCorrection: () => {
      if (!pending) return
      mutation.mutate({
        body: { ...pending.body, confirmCorrection: true },
        onSaved: pending.onSaved,
      })
    },
    cancelCorrection: () => setPending(null),
    isPending: mutation.isPending,
  }
}
